<template>
  <div class="min-h-screen transition-colors duration-300 bg-slate-50/50 dark:bg-slate-950 font-sans text-slate-800 dark:text-slate-200 flex">

    <!-- Backdrop móvil: cierra el menú al tocar fuera -->
    <div
      v-if="isSidebarOpen"
      @click="isSidebarOpen = false"
      class="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm lg:hidden"
      aria-hidden="true"
    ></div>

    <Sidebar :isOpen="isSidebarOpen" />

    <div
      class="flex-1 flex flex-col min-w-0 transition-all duration-300 ease-[cubic-bezier(0.25,0.8,0.25,1)]"
      :class="isSidebarOpen ? 'lg:ml-72' : 'lg:ml-20'"
    >

      <header class="h-16 sticky top-0 z-20 px-8 flex items-center justify-between
                     bg-white/70 backdrop-blur-xl border-b border-slate-200/60
                     dark:bg-slate-900/80 dark:border-slate-800
                     supports-[backdrop-filter]:bg-white/60">

        <!-- LADO IZQUIERDO: Menú y Migas de Pan -->
        <div class="flex items-center gap-4">
          <button
            @click="toggleSidebar"
            class="p-2 -ml-2 rounded-lg transition-colors
                   text-slate-500 hover:text-brand-600 hover:bg-brand-50
                   dark:text-slate-400 dark:hover:text-brand-400 dark:hover:bg-brand-500/10"
            aria-label="Alternar menú lateral"
            :aria-expanded="isSidebarOpen ? 'true' : 'false'"
            title="Mostrar u ocultar el menú"
          >
            <Menu class="w-5 h-5" />
          </button>

          <nav aria-label="Ruta de navegación" class="flex items-center gap-2 text-sm">
            <template v-for="(crumb, i) in breadcrumbs" :key="i">
              <span v-if="!crumb.to" class="text-slate-800 dark:text-slate-200 font-semibold font-medium">{{ crumb.label }}</span>
              <router-link v-else :to="crumb.to" class="text-slate-500 hover:text-brand-600 dark:text-slate-400 transition-colors">{{ crumb.label }}</router-link>
              <ChevronRight v-if="i < breadcrumbs.length - 1" class="w-3.5 h-3.5 text-slate-400 shrink-0" />
            </template>
          </nav>
        </div>

        <!-- LADO DERECHO: Indicadores de Sesión y Notificaciones -->
        <div class="flex items-center gap-5">

          <!-- SELECTOR DE SUCURSAL Y FECHA -->
          <div class="hidden sm:flex items-center gap-3 text-[11px] font-bold text-slate-500 dark:text-slate-400">

            <!-- SELECTOR DINÁMICO DE SUCURSAL -->
            <div class="flex items-center gap-1.5 bg-slate-100/80 dark:bg-slate-800/80 px-3 py-1 rounded-full border border-slate-200 dark:border-slate-700">
              <MapPin class="w-3.5 h-3.5 text-brand-600 dark:text-brand-400 shrink-0" />

              <!-- Si tiene más de 1 sucursal permitida, mostramos el SELECT -->
              <select
                v-if="userBranches.length > 1"
                v-model="selectedBranchId"
                @change="handleBranchChange"
                class="bg-transparent uppercase tracking-wider text-black dark:text-white font-bold focus:outline-none cursor-pointer pr-1"
              >
                <option
                  v-for="b in userBranches"
                  :key="b.id"
                  :value="b.id"
                  class="bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 uppercase font-bold"
                >
                  {{ b.name }}
                </option>
              </select>

              <!-- Si solo tiene 1 sucursal, mostramos Texto Simple -->
              <span v-else class="uppercase tracking-wider text-black dark:text-white">
                {{ currentBranchName }}
              </span>
            </div>

            <!-- Fecha Sistema -->
            <div class="flex items-center gap-1.5 bg-slate-100/80 dark:bg-slate-800/80 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700">
              <Calendar class="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
              <span class="uppercase tracking-wider text-black dark:text-white">{{ systemDate }}</span>
            </div>
          </div>

          <!-- Campana de Notificaciones -->
          <div class="relative">
            <button
              @click="toggleNotificationsPanel"
              class="relative p-2 rounded-full transition-colors
                     text-slate-400 hover:text-slate-600 hover:bg-slate-100
                     dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800"
              aria-label="Notificaciones"
              title="Notificaciones"
            >
              <Bell class="w-5 h-5" />
              <span
                v-if="pushSubscribed"
                class="absolute top-2 right-2 w-2 h-2 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-900"
              ></span>
            </button>

            <!-- Panel de notificaciones (activar/desactivar push) -->
            <div
              v-if="showNotificationsPanel"
              class="fixed inset-0 z-30"
              @click="showNotificationsPanel = false"
            ></div>
            <div
              v-if="showNotificationsPanel"
              class="absolute right-0 mt-2 w-80 rounded-xl border border-slate-200 dark:border-slate-700
                     bg-white dark:bg-slate-900 shadow-xl p-4 z-40"
            >
              <div class="flex items-center gap-2 mb-2">
                <Bell class="w-4 h-4 text-brand-600 dark:text-brand-400" />
                <h3 class="text-sm font-bold text-slate-800 dark:text-slate-100">Notificaciones</h3>
              </div>

              <!-- Navegador sin soporte -->
              <p v-if="!pushSupported" class="text-xs text-slate-500 dark:text-slate-400">
                Tu navegador no soporta notificaciones push.
              </p>

              <!-- Permiso bloqueado por el usuario -->
              <p v-else-if="pushPermission === 'denied'" class="text-xs text-slate-500 dark:text-slate-400">
                Bloqueaste las notificaciones. Habilítalas para este sitio desde la configuración del navegador.
              </p>

              <!-- Ya suscrito -->
              <template v-else-if="pushSubscribed">
                <p class="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  Notificaciones activadas en este dispositivo. Recibirás avisos de pagos, créditos y desembolsos.
                </p>
                <button
                  @click="handleDisablePush"
                  :disabled="pushBusy"
                  class="w-full px-3 py-2 rounded-lg text-xs font-bold transition-colors
                         bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50
                         dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  {{ pushBusy ? 'Procesando…' : 'Desactivar notificaciones' }}
                </button>
              </template>

              <!-- Pendiente de activar -->
              <template v-else>
                <p class="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  Activa las notificaciones para recibir avisos de pagos aplicados, créditos aprobados y desembolsos.
                </p>
                <button
                  @click="handleEnablePush"
                  :disabled="pushBusy"
                  class="w-full px-3 py-2 rounded-lg text-xs font-bold text-white transition-colors
                         bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
                >
                  {{ pushBusy ? 'Activando…' : 'Activar notificaciones' }}
                </button>
              </template>

              <p v-if="pushError" class="mt-2 text-[11px] text-red-500">{{ pushError }}</p>
            </div>
          </div>
        </div>
      </header>

      <main class="flex-1 p-8">
        <slot></slot>
      </main>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import Sidebar from '@/components/layout/Sidebar.vue';
import { Menu, Bell, MapPin, Calendar, ChevronRight } from 'lucide-vue-next';
import { useBranchStore } from '@/stores/branchStore';
import { useSessionStore } from '@/stores/sessionStore';
import { listBranches } from '@/services/companyService';
import { http, getErrorMessage } from '@/services/http';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { usePushNotifications } from '@/composables/usePushNotifications';

const { formatDateDisplay } = useDateFormatter();
// En mobile el sidebar inicia cerrado (off-canvas); en escritorio, abierto.
const isSidebarOpen = ref(window.innerWidth >= 1024);
const toggleSidebar = () => isSidebarOpen.value = !isSidebarOpen.value;

// --- Migas de pan: reflejan la ruta real (meta.title + meta.module) ---
const route = useRoute();

// Al navegar (o si el ancho baja a mobile) replegamos el menú.
watch(
  () => route.path,
  () => {
    if (window.innerWidth < 1024) isSidebarOpen.value = false;
  }
);

const onWindowResize = () => {
  if (window.innerWidth < 1024) isSidebarOpen.value = false;
};

// Etiqueta de sección por módulo (misma taxonomía que menuSections en routes.js).
const moduleLabels = {
  core: 'Visión Gerencial',
  setup: 'Configuración',
  origination: 'Créditos',
  portfolio: 'Reportes de Cartera',
  customers: 'Gestión Clientes',
  treasury: 'Caja',
  accounting: 'Contabilidad y Finanzas',
  collections: 'Cobranza',
};

const breadcrumbs = computed(() => {
  const title = route.meta?.title;
  if (route.name === 'home' || !title) return [{ label: 'Inicio', to: '/' }];
  const crumbs = [{ label: 'Inicio', to: '/' }];
  const mod = route.meta?.module ? moduleLabels[route.meta.module] : null;
  if (mod) crumbs.push({ label: mod });
  crumbs.push({ label: title });
  return crumbs;
});

const systemDate = ref('');

// Fecha de trabajo del sistema: siempre se consulta al backend (GET /system_date),
// nunca al reloj del navegador. El formato de la fecha es "YYYY-MM-DD".
const formatSystemDate = (iso) => formatDateDisplay(iso);

// Consulta la fecha de trabajo de la compañía al backend.
const loadSystemDate = async (companyId) => {
  if (!companyId) {
    systemDate.value = '';
    return;
  }
  try {
    const res = await http.get(`/system_date?company_id=${companyId}`);
    systemDate.value = formatSystemDate(res.data?.current_date);
  } catch (error) {
    console.error('Error al obtener la fecha del sistema:', getErrorMessage(error));
    systemDate.value = '';
  }
};

// Estado centralizado (A2): sucursal activa + sesión reactiva.
const branchStore = useBranchStore();
const { selectedBranchId, userBranches, currentBranchName, init, setBranches, switchBranch } = branchStore;
const sessionStore = useSessionStore();

// --- Notificaciones Web Push (VAPID) ---
const {
  supported: pushSupported,
  permission: pushPermission,
  busy: pushBusy,
  subscribed: pushSubscribed,
  enable: enablePush,
  disable: disablePush,
  refresh: refreshPush,
} = usePushNotifications();

const showNotificationsPanel = ref(false);
const pushError = ref('');

const toggleNotificationsPanel = async () => {
  showNotificationsPanel.value = !showNotificationsPanel.value;
  if (showNotificationsPanel.value) {
    pushError.value = '';
    await refreshPush();
  }
};

const handleEnablePush = async () => {
  pushError.value = '';
  try {
    await enablePush();
  } catch (e) {
    pushError.value = e.message || 'No se pudieron activar las notificaciones.';
  }
};

const handleDisablePush = async () => {
  pushError.value = '';
  try {
    await disablePush();
  } catch (e) {
    pushError.value = e.message || 'No se pudieron desactivar las notificaciones.';
  }
};

onMounted(async () => {
  window.addEventListener('resize', onWindowResize);

  // 1. Estado de sucursal a partir del usuario en sesión
  const context = init();

  if (context) {
    try {
      // 2. Cargar las sucursales de la compañía (vía servicio)
      const res = await listBranches({ company: context.companyId });
      const allBranches = Array.isArray(res.data) ? res.data : (res.data.data || []);

      // 3. Filtrar únicamente las sucursales contenidas en allowed_branches
      setBranches(allBranches, context.allowedIds, context.activeId);
    } catch (error) {
      console.error('Error al obtener la lista de sucursales:', getErrorMessage(error));
      currentBranchName.value = `Agencia #${context.activeId}`;
    }
  }

  // Fecha del sistema (viene del backend, no del reloj del navegador)
  await loadSystemDate(context?.companyId);

  // Estado de notificaciones push (no bloquea la carga; tiene timeout interno)
  refreshPush();
});

onUnmounted(() => {
  window.removeEventListener('resize', onWindowResize);
});

// Cambiar la sucursal activa (RX-4): re-hidratación reactiva del contexto sin
// `window.location.reload()`. branchStore.switchBranch persiste la sucursal en
// SecureStorage e incrementa `branchVersion`; App.vue remonta el <router-view>
// con esa clave y cada vista vuelve a obtener sus datos para la sucursal nueva.
const handleBranchChange = () => {
  switchBranch(selectedBranchId.value);
  sessionStore.refresh();
};
</script>